"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
var types_1 = require("./types");
var dispatchProxy_1 = __importDefault(require("./dispatchProxy"));
var Track = /** @class */ (function () {
    function Track() {
    }
    /**
     * Inform SAS, GA and Kibana that your application is loaded and weather it is available to the user or not.
     * ```typescript
     * import {track} from 'c9-tracking';
     * ...
     * track.ready(true);
     * ```
     * @returns {Promise<NULL|string>}
     * @param available set to false if the application is hidden or disabled
     * @param label describe the availability with a human understandable short description
     * @param consumerOverwrite overwrites to witch consumers is tracked, if none provide it will track all consumers.
     */
    Track.prototype.ready = function (available, label, consumerOverwrite) {
        if (available === void 0) { available = true; }
        return dispatchProxy_1.default(__assign(__assign({}, consumerOverwrite), { type: [types_1.trackingTypes.behaviour], action: 'app-ready', items: [{ key: 'available', value: available }], label: label }));
    };
    /**
     * Tell SAS360, GA and Kibana that a conversion happened.
     * It could be an email signup, sending a message or buying a product
     * ```typescript
     * import {track} from 'c9-tracking';
     * ...
     * track.convert([
     *    { key: 'email', value: 'signup' },
     *    { key: 'list', value: 'new-list-123' }
     * ]);
     * track.convert('User bought an insurance');
     * ```
     * @returns {Promise<NULL|string>}
     * @param items
     * @param label describe the conversion with a human understandable short description
     * @param consumerOverwrite overwrites to witch consumers is tracked, if none provide it will track all consumers.
     */
    Track.prototype.convert = function (items, label, consumerOverwrite) {
        if (typeof items === 'string')
            items = [{ key: 'message', value: items }];
        return dispatchProxy_1.default(__assign(__assign({}, consumerOverwrite), { type: [types_1.trackingTypes.behaviour], action: 'app-convert', items: items,
            label: label }));
    };
    /**
     * Inform SAS, GA and Kibana about where in a multistep flow the customer are. The aim is to visualyze a funnel
     * ```typescript
     * import {track} from 'c9-tracking';
     * ...
     * track.step(2, 5);  // use numbered steps ... or
     * track.step('Supply user details', 5); // use named steps
     * ```
     * @returns {Promise<NULL|string>}
     * @param current current step - name or number
     * @param total number of total steps
     * @param label describe the funnel with a human understandable short description
     * @param consumerOverwrite overwrites to witch consumers is tracked, if none provide it will track all consumers.
     */
    Track.prototype.step = function (current, total, label, consumerOverwrite) {
        return dispatchProxy_1.default(__assign(__assign({}, consumerOverwrite), { type: [types_1.trackingTypes.behaviour], action: 'app-step', items: [
                { key: 'current', value: current },
                { key: 'total', value: total },
            ], label: label }));
    };
    /**
     * Tell SAS360, GA and Kibana that an interaction occoured with your application.
     * Ex. open FAQ element, typing in a field...
     * ```typescript
     * import {track} from 'c9-tracking';
     * ...
     * track.interact([
     *   { key: 'faq', value: 'open' },
     *   { key: 'faq-id', value: '123' }
     * ], {
     *     consumerOverwrite: [ga, kibana]   *
     *    }
     * );
     * track.interact('log out');  // or simple string
     * ```
     * @returns {Promise<NULL|string>}
     * @param items
     * @param label describe the interaction with a human understandable short description
     * @param consumerOverwrite overwrites to witch consumers is tracked, if none provide it will track all consumers.
     */
    Track.prototype.interact = function (items, label, consumerOverwrite) {
        if (typeof items === 'string')
            items = [{ key: 'message', value: items }];
        return dispatchProxy_1.default(__assign(__assign({}, consumerOverwrite), { type: [types_1.trackingTypes.behaviour], action: 'app-interact', items: items,
            label: label }));
    };
    /**
     * Tell SAS360, GA and Kibana that the user navigated inside your SPA - aka virtual pageview
     * ```typescript
     * track.navigate(document.URL + 'get-quote', 'Show quote to customer');
     * ```
     * @returns {Promise<NULL|string>}
     * @param route full url + your virtual roucte
     * @param label
     * @param consumerOverwrite overwrites to witch consumers is tracked, if none provide it will track all consumers.
     */
    Track.prototype.navigate = function (route, pageTitle, label, consumerOverwrite) {
        var items = [{ key: 'route', value: route }];
        if (pageTitle)
            items.push({ key: 'pageTitle', value: pageTitle });
        return dispatchProxy_1.default(__assign(__assign({}, consumerOverwrite), { type: [types_1.trackingTypes.behaviour], action: 'app-navigate', items: items,
            label: label }));
    };
    /**
     * Push additional information to SAS, GA and Kibana.
     * It could be calculations of intermediate prices or data fetched from other services
     * ```typescript
     * imprt {track} from 'c9-tracking';
     * ...
     * track.info([
     *    { key: 'annual-pay', value: '1230' },
     *    { key: 'monthly-pay', value: '123' }
     * ]);
     * track.info('bla bla bla');
     * ```
     * @returns {Promise<NULL|string>}
     * @param items
     * @param label describe the additional information with a human understandable short description
     * @param consumerOverwrite overwrites to witch consumers is tracked, if none provide it will track all consumers.
     */
    Track.prototype.info = function (items, label, consumerOverwrite) {
        if (typeof items === 'string')
            items = [{ key: 'message', value: items }];
        return dispatchProxy_1.default(__assign(__assign({}, consumerOverwrite), { type: [types_1.trackingTypes.behaviour], action: 'app-info', items: items,
            label: label }));
    };
    /**
     * Track an error. Remember this is NOT for debugging so dont enter technical info in the message
     * Ex. invalid form field value...
     * ```js
     * import {track} from 'c9-tracking';
     * import {errorTypes} from 'c9-tracking/lib/types';
     * ...
     * track.fault('email validation failed', errorTypes.user, 'Sign-up flow');
     * ```
     * @returns {Promise<NULL|string>}
     * @param message describe what is the issue
     * @param type is the error a technical error (ex. endpoint timeout) or a human error (ex. form validation failed)
     * @param label describe the interaction with a human understandable short description
     * @param technicalInfo add technical info here - if you must
     * @param consumerOverwrite overwrites to witch consumers is tracked, if none provide it will track all consumers.
     */
    Track.prototype.fault = function (message, type, label, technicalInfo, consumerOverwrite) {
        var items = [{ key: 'message', value: message }];
        if (technicalInfo)
            items.push({ key: 'technical', value: technicalInfo });
        return dispatchProxy_1.default(__assign(__assign({}, consumerOverwrite), { type: [types_1.trackingTypes.behaviour], action: 'app-error-' + type, items: items,
            label: label }));
    };
    /**
     * Track a form submit.
     * Ex.
     * ```js
     * const field1 = { id: 'name', value: name };
     * const field2 = { id: 'age', value: age };
     * const field3 = { id: 'addr', value: addr };
     * track.form('form-id', [field1, field2, field3]);
     * ```
     * @param name
     * @param fields
     * @param id
     * @param attempts
     * @param consumerOverwrite overwrites to witch consumers is tracked, if none provide it will track all consumers.
     */
    Track.prototype.form = function (id, fields, name, attempts, label, consumerOverwrite) {
        var items = [
            { key: 'id', value: id },
            { key: 'fields', value: fields },
        ];
        if (name)
            items.push({ key: 'name', value: name });
        if (attempts)
            items.push({ key: 'attempts', value: attempts });
        if (label)
            items.push({ key: 'label', value: label });
        if (name)
            items.push({ key: 'name', value: name });
        return dispatchProxy_1.default(__assign(__assign({}, consumerOverwrite), { type: [types_1.trackingTypes.behaviour], action: 'app-form', items: items,
            label: label }));
    };
    return Track;
}());
exports.default = new Track();
